import React from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { useContextHook } from '../store/context'
import { useTheme } from '@mui/material/styles'

import CircularProgress from '@mui/material/CircularProgress'
import LoadingButton from '@mui/lab/LoadingButton'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Link from '@mui/material/Link'
import { Invite, CreateUser, ValidationError, MachineInvite } from '../types/signup'
import { login, get, post, setPageTitle } from '../utils/service'
import Alert from '../components/alert/alert'
import NewMachines from '../components/invite/newMachines'
import { Button, IconButton, ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import NumberField from '../components/fields/numberField'
import { PagedResponse } from '../types/pagedResponse'
import { User } from '../types/user'
import { Customer } from '../types/customer'
import { feetToMeterConverter } from '../utils/distanceUtils'
import MuiLink from '@mui/material/Link'

const SignupView = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams()
  const { state } = useContextHook()
  const theme = useTheme()
  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))
  const isScreenBelowLgBreakpoint = useMediaQuery(theme.breakpoints.down('lg'))
  const isScreenBelowXlBreakpoint = useMediaQuery(theme.breakpoints.down('xl'))

  const [isOnLandingPage, setIsOnLandingPage] = React.useState<boolean>(true)
  const [isSigningUp, setIsSigningUp] = React.useState<boolean>(false)
  const [isLoggingIn, setIsLoggingIn] = React.useState<boolean>(false)
  const [isSettingElevation, setIsSettingElevation] = React.useState<boolean>(false)

  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [hasValidInvite, setHasValidInvite] = React.useState<boolean>(false)
  const [isPosting, setIsPosting] = React.useState<boolean>(false)
  const [customerName, setCustomerName] = React.useState<string>('')
  const [customerNameError, setCustomerNameError] = React.useState<string>('')
  const [username, setUsername] = React.useState<string>('')
  const [usernameError, setUsernameError] = React.useState<string>('')
  const [userEmail, setUserEmail] = React.useState<string>('')
  const [userEmailError, setUserEmailError] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [passwordError, setPasswordError] = React.useState<string>('')
  const [passwordAgain, setPasswordAgain] = React.useState<string>('')
  const [passwordAgainError, setPasswordAgainError] = React.useState<string>('')
  const [machineCount, setMachineCount] = React.useState<number>(0)
  const [machines, setMachines] = React.useState<MachineInvite[]>()
  const [loginError, setLoginError] = React.useState<string>('')
  const [customers, setCustomers] = React.useState<Customer[]>()
  const [useFeet, setUseFeet] = React.useState<boolean>(false)
  const [individualElevation, setIndividualElevation] = React.useState<boolean>(false)
  const [completeSetUpError, setCompleteSetUpError] = React.useState<boolean>(false)

  const updateFormValidationErrors = (errors: ValidationError): void => {
    if (errors.user?.username) {
      setUsernameError(errors.user.username)
    }
    if (errors.user?.email) {
      setUserEmailError(errors.user.email)
    }
    if (errors.user?.password) {
      setPasswordError(errors.user.password)
    }
    if (errors.customer?.name) {
      setCustomerNameError(errors.customer.name)
    }
  }

  const doLogin = (username: string | null) => {
    if (username) {
      setIsPosting(true)
      login(
        username,
        password,
        () => {
          setLoginError('')
          getCustomerID(username)
        },
        (statusCode: number | undefined, invalidGrant: boolean, noResponse: boolean, baseUrl: string | undefined) => {
          setIsPosting(false)
          if ((statusCode === 400 || statusCode === 401) && invalidGrant) {
            setLoginError(t('loginview.unauthorized'))
          } else if (statusCode) {
            setLoginError(t('loginview.unexpectedresponse', { statusCode: statusCode }))
          } else if (noResponse && baseUrl) {
            setLoginError(t('loginview.couldnotcontact', { baseUrl: baseUrl }))
          } else {
            setLoginError(t('loginview.genericerror'))
          }
        }
      )
    }
  }
  const getCustomerID = (username: string | null) => {
    get<PagedResponse<User>>(`users/?username=${username}`).then((response) => {
      setCustomers(response.data.results[0].customers)
      setIsPosting(false)
      setIsSettingElevation(true)
    })
  }
  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setIsPosting(true)
    post<CreateUser, Invite>(`/invites/${params.inviteId}/create_user/`, {
      customer: {
        name: customerName,
      },
      user: {
        username: username,
        email: userEmail,
        password: password,
      },
    })
      .then((response) => {
        setIsPosting(false)
        doLogin(response.data.username)
      })
      .catch((err) => {
        setIsPosting(false)
        if (err.response && err.response.status === 400) {
          updateFormValidationErrors(err.response.data)
        }
      })
  }

  React.useEffect(() => {
    setPageTitle(t('signup.pagetitle'))
  }, [t])

  React.useEffect(() => {
    setIsLoading(true)
    get<Invite>(`/invites/${params.inviteId}/`)
      .then((response) => {
        setIsLoading(false)
        setMachineCount(response.data.machines.length)
        setMachines(response.data.machines)
        setHasValidInvite(true)
      })
      .catch(() => {
        setIsLoading(false)
        setHasValidInvite(false)
      })
  }, [params.inviteId])

  React.useEffect(() => {
    if (password) {
      setPasswordError('')
      setPasswordAgainError('')

      if (passwordAgain && password !== passwordAgain) {
        setPasswordAgainError(t('signup.errorpasswordsdonotmatch'))
      }
    }
  }, [t, password, passwordAgain])

  const supportLink = <Link href="/support/ticket">"support"</Link>

  const completeSetUp = () => {
    const customer_id = state.currentCustomer?.id || (customers && customers.length > 0 ? customers[0].id : undefined)
    let updatedMachines
    if (useFeet) {
      const m = machines?.map((machine) => {
        if (machine.elevation !== null) {
          return { ...machine, elevation: feetToMeterConverter(machine.elevation) }
        }
        return machine
      })
      updatedMachines = m
    } else {
      updatedMachines = machines
    }
    if (isSigningUp) {
      const url = `/machines/set_elevation/`
      const data = { machines: updatedMachines }
      post(url, data)
        .then(() => {
          window.location.href = '/'
        })
        .catch(() => {
          setCompleteSetUpError(true)
          setIsPosting(false)
        })
    } else {
      const url = `/invites/${params.inviteId}/assign_to_current/`
      const data = {
        id: customer_id,
        machines: updatedMachines,
      }
      console.log(data)
      post(url, data)
        .then(() => {
          window.location.href = '/'
        })
        .catch(() => {
          setCompleteSetUpError(true)
          setIsPosting(false)
        })
    }
  }
  const backToLandingPage = () => {
    setIsSettingElevation(false)
    setIndividualElevation(false)
    setIsSigningUp(false)
    setIsLoggingIn(false)
    setIsOnLandingPage(true)
  }
  return (
    <>
      {isLoading && (
        <CircularProgress size={120} sx={{ position: 'absolute', top: '40vh', left: '47.5vw', zIndex: 1 }} />
      )}
      {!isLoading && !hasValidInvite && (
        <Stack sx={{ position: 'absolute', top: '40vh', alignSelf: 'center', zIndex: 1 }}>
          <Alert severity="error">
            <Trans i18nKey="signup.invalidinvite" components={{ supportLink }}></Trans>
          </Alert>
        </Stack>
      )}
      <Stack
        component="main"
        direction={'row'}
        sx={{
          mt: -2,
          filter: isLoading || !hasValidInvite ? 'blur(3px)' : 'undefined',
          position: 'relative',
        }}
      >
        {!isScreenBelowMdBreakpoint && (
          <NewMachines machines={machines} machineCount={machineCount} individualElevation={individualElevation} />
        )}
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            alignItems: 'center',
            backgroundColor: 'background.signup',
            px: isScreenBelowLgBreakpoint ? 2 : 'undefined',
          }}
        >
          {!isOnLandingPage && (
            <IconButton onClick={backToLandingPage} aria-label="ArrowBack" sx={{ left: '-23vw', top: 20 }}>
              <ArrowBack fontSize="large" />
            </IconButton>
          )}
          <Stack
            sx={{
              mt: isScreenBelowLgBreakpoint ? '5%' : isScreenBelowXlBreakpoint ? '10%' : '20%',
              width: isScreenBelowLgBreakpoint ? (isScreenBelowMdBreakpoint ? '90vw' : '50vw') : '25vw',
              mb: '10%',
            }}
          >
            {isOnLandingPage && (
              <Stack sx={{ mt: 3 }}>
                <Typography variant="h4">
                  {isScreenBelowMdBreakpoint &&
                    t('newmachine.connectyourmachines').replace('{0}', machineCount > 1 ? 's' : '')}
                  {!isScreenBelowMdBreakpoint && t('newmachine.loginandconnect')}
                </Typography>
                {!!state.user && (
                  <>
                    <Typography variant="body1" sx={{ mt: 3 }}>
                      {t('newmachine.alreadyloggedin').replace('{0}', state.user.username)}
                    </Typography>
                    <Button
                      color="secondary"
                      onClick={() => {
                        setIsSettingElevation(true)
                        setIsOnLandingPage(false)
                      }}
                      variant="contained"
                    >
                      {t('newmachine.connectmachinetothisaccount').replace('{0}', machineCount > 1 ? 's' : '')}
                    </Button>
                  </>
                )}
                <Typography variant="body1" sx={{ mt: 3 }}>
                  {t('newmachine.createnewuser')}
                </Typography>
                <Button
                  onClick={() => {
                    setIsSigningUp(true)
                    setIsOnLandingPage(false)
                  }}
                  sx={{ mb: 3 }}
                  variant="contained"
                >
                  {t('signup.signup')}
                </Button>
                {!state.user && (
                  <>
                    <Typography variant="body1">{t('newmacine.connecttoexisting')}</Typography>
                    <Button
                      onClick={() => {
                        setIsLoggingIn(true)
                        setIsOnLandingPage(false)
                      }}
                      variant="contained"
                    >
                      {t('loginview.login')}
                    </Button>
                  </>
                )}
              </Stack>
            )}
            {isSigningUp && !isSettingElevation && (
              <form onSubmit={onFormSubmit}>
                <Stack spacing={3}>
                  <Typography variant="h4" component="h1">
                    {t('signup.signup')}
                  </Typography>
                  <Typography variant="body1">{t('newmachine.newroasterwillbeconnectedtoaccount')}</Typography>
                  <TextField
                    fullWidth
                    label={t('signup.customername')}
                    variant="outlined"
                    helperText={customerNameError ? customerNameError : null}
                    error={!!customerNameError}
                    required
                    value={customerName}
                    inputProps={{
                      autoCapitalize: 'off',
                      autoCorrect: 'off',
                    }}
                    onChange={(event) => {
                      setCustomerName(event.target.value)
                      setCustomerNameError('')
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('signup.useremail')}
                    variant="outlined"
                    helperText={userEmailError ? userEmailError : null}
                    error={!!userEmailError}
                    required
                    value={userEmail}
                    onChange={(event) => {
                      setUserEmail(event.target.value)
                      setUserEmailError('')
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('signup.username')}
                    variant="outlined"
                    helperText={usernameError ? usernameError : null}
                    error={!!usernameError}
                    autoComplete="off"
                    required
                    value={username}
                    onChange={(event) => {
                      setUsername(event.target.value)
                      setUsernameError('')
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('signup.password')}
                    variant="outlined"
                    type="password"
                    helperText={passwordError ? passwordError : null}
                    error={!!passwordError}
                    required
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value)
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('signup.passwordagain')}
                    variant="outlined"
                    type="password"
                    helperText={passwordAgainError ? passwordAgainError : null}
                    error={!!passwordAgainError}
                    required
                    value={passwordAgain}
                    onChange={(event) => {
                      setPasswordAgain(event.target.value)
                    }}
                  />
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    sx={{ width: '20%', alignSelf: 'end' }}
                    loading={isPosting}
                  >
                    {t('newmachine.connect')}
                  </LoadingButton>
                </Stack>
              </form>
            )}
            {isLoggingIn && !isSettingElevation && (
              <Stack spacing={3}>
                <Typography variant="h4" component="h1">
                  {t('loginview.login')}
                </Typography>
                <Typography variant="body1">{t('newmachine.newroasterwillbeconnectedtoaccount')}</Typography>
                <TextField
                  fullWidth
                  label={t('signup.username')}
                  variant="outlined"
                  helperText={usernameError ? usernameError : null}
                  error={!!usernameError}
                  autoComplete="off"
                  required
                  value={username}
                  onChange={(event) => {
                    setUsername(event.target.value)
                    setUsernameError('')
                  }}
                />
                <TextField
                  fullWidth
                  label={t('signup.password')}
                  variant="outlined"
                  type="password"
                  helperText={passwordError ? passwordError : null}
                  error={!!passwordError}
                  required
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value)
                  }}
                />
                {loginError && isLoggingIn && <Alert severity="error">{loginError}</Alert>}
                <MuiLink
                  href={`${process.env.REACT_APP_DJANGO_URL}/accounts/password_reset/`}
                  sx={{ alignSelf: 'end' }}
                >
                  {t('loginview.forgotpassword')}
                </MuiLink>
                <LoadingButton
                  variant="contained"
                  sx={{ width: '20%', alignSelf: 'end' }}
                  loading={isPosting}
                  onClick={() => {
                    doLogin(username)
                  }}
                >
                  {t('newmachine.connect')}
                </LoadingButton>
              </Stack>
            )}
            {isSettingElevation && (
              <Stack spacing={3}>
                <Typography variant="h4" component="h1">
                  {t('newmachine.setelevation')}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {t('newmachine.setelevationhelpertextpt1')}
                  <Link
                    href="https://www.roestcoffee.com/guides/all-about-the-service-menu"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('newmachine.servicemenu')}
                  </Link>
                </Typography>

                <ToggleButtonGroup
                  sx={{ justifyContent: 'end' }}
                  onChange={(event: React.MouseEvent<HTMLElement>, value: string) => {
                    setUseFeet(JSON.parse(value))
                  }}
                  value={useFeet.toString()}
                  exclusive={true}
                  size="small"
                >
                  <ToggleButton value="false" color={'primary'}>
                    {t('newmachine.meters')}
                  </ToggleButton>
                  <ToggleButton value="true" color={'primary'}>
                    {t('newmachine.feet')}
                  </ToggleButton>
                </ToggleButtonGroup>
                {!individualElevation && (
                  <NumberField
                    numValue={
                      machines && machines.length > 0 && machines[0].elevation != null ? machines[0].elevation : null
                    }
                    placeholder="0"
                    valueSuffix={useFeet ? 'ft' : 'm'}
                    minNumValue={0}
                    maxNumValue={useFeet ? 29035 : 8848}
                    decimalPlaces={0}
                    onBlur={(newValue: number) => {
                      const updatedMachines = machines?.map((machine) => ({
                        ...machine,
                        elevation: newValue,
                      }))
                      setMachines(updatedMachines)
                    }}
                  />
                )}
                {individualElevation && (
                  <>
                    {machines?.map((m, index) => (
                      <Stack direction={'row'}>
                        <Typography variant="h5" sx={{ alignSelf: 'center', mr: 2 }}>
                          {index + 1}
                        </Typography>
                        <NumberField
                          label={m.machine_name}
                          numValue={m.elevation || null}
                          placeholder="0"
                          valueSuffix={useFeet ? 'ft' : 'm'}
                          minNumValue={0}
                          maxNumValue={useFeet ? 29035 : 8848}
                          decimalPlaces={0}
                          onBlur={(newValue: number) => {
                            const updatedMachines = machines.map((machine) =>
                              machine.machine_id === m.machine_id ? { ...machine, elevation: newValue } : machine
                            )
                            setMachines(updatedMachines)
                          }}
                          sx={{ width: '100%' }}
                        />
                      </Stack>
                    ))}
                  </>
                )}
                {completeSetUpError && <Alert severity="error">{t('newmachine.completesetuperror')}</Alert>}
                {machineCount > 1 && (
                  <Link
                    onClick={() => {
                      if (individualElevation) setIndividualElevation(false)
                      else setIndividualElevation(true)
                    }}
                    sx={{ alignSelf: 'end', mt: -10 }}
                  >
                    {individualElevation ? t('newmachine.commonelevation') : t('newmachine.individualelevation')}
                  </Link>
                )}
                <LoadingButton
                  onClick={completeSetUp}
                  variant="contained"
                  sx={{ width: '20%', alignSelf: 'end' }}
                  loading={isPosting}
                >
                  {t('newmachine.connect')}
                </LoadingButton>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </>
  )
}

export default SignupView
